import { jiraStatusMapper, jiraStatusCategoryMapper, statusCategoryMapper } from "@Helpers";
import type { TranslatedString, TranslationKey } from "@i18n";

/** The strictly-typed `t` this module's callers pass in. */
type Translate = (key: TranslationKey, options?: Record<string, unknown>) => TranslatedString;

/** One ADF text mark, produced either from an inline HTML tag or from an inline colour style. */
type AdfMark = { type: string; attrs?: { href?: string; color?: string } };

/** An ADF inline node — a text run (with its marks) or a hard break. */
type AdfInlineNode = { type: string; text?: string; marks?: AdfMark[] };

/** An ADF block node produced from one HTML element. `content` holds inline nodes for the leaf
 *  blocks and nested block nodes for lists, hence `unknown[]`. */
type AdfBlockNode = { type: string; attrs?: { level?: number }; content?: unknown[] };

const MUTED_STATUS_COLORS: Record<string, { bg: string; border: string }> = {
  "#666a84": { bg: "#D8D9E0", border: "#666A84" }, // To Do (grey)
  "#ffcf55": { bg: "#FFF3D4", border: "#FFCF55" }, // In Progress (yellow)
  "#45cf80": { bg: "#C5E5D3", border: "#189A50" }, // Done — positive (green)
  "#e75555": { bg: "#F9D4D4", border: "#E75555" }, // Done — negative (red)
  "#1080d6": { bg: "#BFE6F3", border: "#009DD0" }, // Done — closed (blue)
};

export const mutedStatusColors = (hex: string): { bg: string; border: string } =>
  MUTED_STATUS_COLORS[hex] || { bg: "#ECECEF", border: "#B3B5C2" };

export const editorToolbarSettings: { items: string[] } = {
  items: [
    "Undo",
    "Redo",
    "|",
    "OrderedList",
    "UnorderedList",
    "|",
    "cut",
    "copy",
    "paste",
    "|",
    "FontColor",
    "Bold",
    "Italic",
    "Underline",
    "StrikeThrough",
  ],
};

export const convertHtmlToAdf = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // `getContext` is only null when the canvas already has a different context type; a freshly
  // created element cannot. `fillStyle` reads back as the wider gradient/pattern union, but the
  // browser normalises an assigned colour string to a string.
  const cssColorToHex = (color: string): string | null => {
    const ctx = document.createElement("canvas").getContext("2d")!;
    ctx.fillStyle = color;
    return (ctx.fillStyle as string).startsWith("#") ? (ctx.fillStyle as string) : null;
  };

  const getTagMark = (el: Element) => {
    const tag = el.tagName.toLowerCase();
    switch (tag) {
      case "strong":
      case "b":
        return { type: "strong" };
      case "em":
      case "i":
        return { type: "em" };
      case "u":
        return { type: "underline" };
      case "s":
        return { type: "strike" };
      case "a": {
        const href = el.getAttribute("href");
        return href ? { type: "link", attrs: { href } } : null;
      }
      default:
        return null;
    }
  };

  const getStyleMarks = (style: string | null) => {
    if (!style?.includes("color")) return [];
    const colorMatch = RegExp(/color\s*:\s*([^;]+)/i).exec(style);
    if (!colorMatch) return [];
    const hex = cssColorToHex(colorMatch[1]?.trim() ?? "");
    return hex ? [{ type: "textColor", attrs: { color: hex } }] : [];
  };

  const getMarks = (node: Node | null) => {
    const marks: AdfMark[] = [];
    // `instanceof Element` is the same test the loop used to spell as `nodeType === 1`, but it also
    // narrows, so the walk needs no assertion. `parentElement` is an element by construction.
    let el = node instanceof Element ? node : null;

    while (el) {
      const tagMark = getTagMark(el);
      if (tagMark) marks.push(tagMark);

      marks.push(...getStyleMarks(el.getAttribute("style")));
      el = el.parentElement;
    }

    return marks;
  };

  const findClosestElement = (node: Node | null) => {
    while (node && node.nodeType !== 1) {
      node = node.parentNode;
    }
    return node;
  };

  const toTextNode = (node: Node): AdfInlineNode | null => {
    const text = node.textContent?.trim();
    if (!text) return null;

    const nearestElement = findClosestElement(node);
    return {
      type: "text",
      text,
      marks: getMarks(nearestElement),
    };
  };

  // Explicit return type: the function is recursive, so inference cannot resolve it on its own.
  const toInlineNodes = (parent: Node): (AdfInlineNode | null)[] =>
    Array.from(parent.childNodes)
      .map((child) => {
        if (child.nodeType === Node.TEXT_NODE) return toTextNode(child);
        if (child instanceof Element && child.tagName.toLowerCase() === "br") return { type: "hardBreak" };
        return toInlineNodes(child);
      })
      .flat()
      .filter(Boolean);

  // Same recursion as above. `instanceof Element` is the narrowing form of the ELEMENT_NODE guard.
  const toADFNode = (node: Node): AdfBlockNode | null => {
    if (!(node instanceof Element)) return null;
    const tag = node.tagName.toLowerCase();

    const inline = toInlineNodes(node);

    switch (tag) {
      case "p":
        return { type: "paragraph", content: inline };
      case "h1":
        return { type: "heading", attrs: { level: 1 }, content: inline };
      case "h2":
        return { type: "heading", attrs: { level: 2 }, content: inline };
      case "h3":
        return { type: "heading", attrs: { level: 3 }, content: inline };
      case "ul":
        return {
          type: "bulletList",
          content: Array.from(node.children).map(toADFNode).filter(Boolean),
        };
      case "ol":
        return {
          type: "orderedList",
          content: Array.from(node.children).map(toADFNode).filter(Boolean),
        };
      case "li":
        return {
          type: "listItem",
          content: [
            {
              type: "paragraph",
              content: inline,
            },
          ],
        };
      default:
        return { type: "paragraph", content: inline };
    }
  };

  const content = Array.from(doc.body.childNodes).map(toADFNode).filter(Boolean);

  return {
    version: 1,
    type: "doc",
    content,
  };
};

export const extractBase64Images = (html: string) => {
  const base64Images: string[] = [];
  const regex = /<img[^>]+src=["'](data:image\/[^"']+)["']/g;
  let match;

  while ((match = regex.exec(html)) !== null) {
    base64Images.push(match[1]!);
  }

  return base64Images;
};

export const removeImgTags = (html: string) => {
  return html.replace(/<img[^>]*>/gi, "");
};

export const getReportedFilter = (isWhiteListed: boolean, t: Translate) => {
  return [
    {
      name: t("Created By Me"),
      value: "createdByMe",
    },
    {
      name: t("Created By Anyone In My Org"),
      value: "createdByOrgUsers",
    },
    {
      name: t("Created By Anyone In My Portal"),
      value: "createdByPortalUsers",
    },
    ...(isWhiteListed
      ? [
          {
            name: t("Created By Anyone In Any Portal"),
            value: "createdByAnyPortalUsers",
          },
        ]
      : []),
  ];
};

export const getStatusFilter = (t?: Translate) => {
  const statusMap = jiraStatusMapper(t);
  const catMap = statusCategoryMapper(t);
  const sample: Record<"TO_DO" | "IN_PROGRESS" | "DONE" | "ALL", { id: string; value: string }[]> = {
    TO_DO: [],
    IN_PROGRESS: [],
    DONE: [],
    ALL: [],
  };
  Object.keys(statusMap).forEach((key: string) => {
    const obj = { id: key, value: statusMap[key]! };
    const categoryId = catMap[key]?.id;
    if (categoryId === "2") sample.TO_DO.push(obj);
    else if (categoryId === "3") sample.IN_PROGRESS.push(obj);
    else if (categoryId === "4") sample.DONE.push(obj);
    sample.ALL.push(obj);
  });
  return sample;
};

export const getStatusCategoryFilter = (t?: Translate) => {
  const catMap = jiraStatusCategoryMapper(t);
  return Object.keys(catMap).map((key: string) => ({ id: key, value: catMap[key] }));
};

export const getRequestTypeFilter = (t: Translate) => {
  return [
    {
      name: t("All Requests"),
      value: "allRequests",
    },
    {
      name: t("Ask A Question"),
      value: "Question",
    },
    {
      name: t("Report A Bug"),
      value: "Report a Bug",
    },
    {
      name: t("Suggest A Feature"),
      value: "Feature Request",
    },
  ];
};
